/**
 * Shopping List Domain Service
 *
 * Domain service for shopping list operations. Implements fetching, merging,
 * updating, and clearing shopping list items using the backend API contract.
 *
 * @module services/domains/ShoppingListDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager } from '../shared';
import type {
  ShoppingList,
  ShoppingListItem,
  ShoppingListItemResponse,
  ShoppingListResponse,
  AddToShoppingListRequest,
  UpdateShoppingListItemRequest,
  EmailShoppingListRequest,
} from '@/types/shoppingList';

/**
 * Shopping list domain-specific error class
 */
export class ShoppingListApiError extends BaseApiError {
  /** Operation that failed */
  public readonly operation: string;

  constructor(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'ShoppingListApiError';
    this.operation = operation;
  }
}

/**
 * Shopping list API endpoints
 */
const ENDPOINTS = {
  LIST: '/shopping-list',
  ITEM: (itemId: string) => `/shopping-list/items/${itemId}`,
  EMAIL: '/shopping-list/email',
} as const;

/**
 * Domain service for shopping list operations
 */
export class ShoppingListDomain extends BaseDomain<ShoppingListApiError> {
  constructor() {
    const apiClient = new ApiClient<ShoppingListApiError>(
      (message, status, fieldErrors, code) => new ShoppingListApiError('API_ERROR', message, status, fieldErrors, code)
    );

    super(apiClient, tokenManager, requestManager);
  }

  /**
   * Get domain name for error messages
   */
  protected getDomainName(): string {
    return 'ShoppingList';
  }

  /**
   * Create domain-specific error instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): ShoppingListApiError {
    return new ShoppingListApiError(operation, message, status, fieldErrors, code);
  }

  /**
   * Fetch the current user's shopping list.
   *
   * @returns Promise<ShoppingList> The user's shopping list
   * @throws ShoppingListApiError When request fails
   */
  async getShoppingList(): Promise<ShoppingList> {
    return this.executeWithDeduplication('getShoppingList', async () => {
      return this.executeWithRetry(async () => {
        try {
          const response = await this.apiClient.get<ShoppingListResponse>(ENDPOINTS.LIST);
          return this.transformShoppingListResponse(response);
        } catch (error) {
          throw this.createErrorFromUnknown(error, 'getShoppingList');
        }
      });
    });
  }

  /**
   * Add or replace items in the shopping list.
   *
   * @param request Add or replace request payload
   * @returns Promise<ShoppingList> Updated shopping list
   * @throws ShoppingListApiError When request fails
   */
  async addItems(request: AddToShoppingListRequest): Promise<ShoppingList> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.post<ShoppingListResponse>(
          ENDPOINTS.LIST,
          this.transformAddItemsRequest(request)
        );
        return this.transformShoppingListResponse(response);
      } catch (error) {
        throw this.createErrorFromUnknown(error, 'addItems');
      }
    });
  }

  /**
   * Update a specific shopping list item.
   *
   * @param itemId Item identifier
   * @param update Update payload
   * @returns Promise<ShoppingListItem> Updated item
   * @throws ShoppingListApiError When request fails
   */
  async updateItem(itemId: string, update: UpdateShoppingListItemRequest): Promise<ShoppingListItem> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.put<ShoppingListItemResponse>(
          ENDPOINTS.ITEM(itemId),
          this.transformUpdateItemRequest(update)
        );
        return this.transformItemResponse(response);
      } catch (error) {
        throw this.createErrorFromUnknown(error, 'updateItem');
      }
    });
  }

  /**
   * Remove a specific shopping list item.
   *
   * @param itemId Item identifier
   * @returns Promise<void>
   * @throws ShoppingListApiError When request fails
   */
  async deleteItem(itemId: string): Promise<void> {
    return this.executeWithRetry(async () => {
      try {
        await this.apiClient.delete(ENDPOINTS.ITEM(itemId));
      } catch (error) {
        throw this.createErrorFromUnknown(error, 'deleteItem');
      }
    });
  }

  /**
   * Clear the shopping list.
   *
   * @returns Promise<void>
   * @throws ShoppingListApiError When request fails
   */
  async clearList(): Promise<void> {
    return this.executeWithRetry(async () => {
      try {
        await this.apiClient.delete(ENDPOINTS.LIST);
      } catch (error) {
        throw this.createErrorFromUnknown(error, 'clearList');
      }
    });
  }

  /**
   * Email the shopping list.
   *
   * @param request Email payload
   * @returns Promise<void>
   * @throws ShoppingListApiError When request fails
   */
  async emailList(request: EmailShoppingListRequest): Promise<void> {
    return this.executeWithRetry(async () => {
      try {
        await this.apiClient.post(ENDPOINTS.EMAIL, request);
      } catch (error) {
        throw this.createErrorFromUnknown(error, 'emailList');
      }
    });
  }

  /**
   * Transform shopping list API response to frontend shape.
   */
  private transformShoppingListResponse(response: ShoppingListResponse): ShoppingList {
    return {
      id: response.id,
      items: response.items.map(item => this.transformItemResponse(item)),
      updatedAt: response.updated_at,
    };
  }

  /**
   * Transform shopping list item response to frontend shape.
   */
  private transformItemResponse(item: ShoppingListItemResponse): ShoppingListItem {
    return {
      id: item.id,
      name: item.name,
      quantity: item.quantity,
      unit: item.unit,
      isChecked: item.is_checked,
    };
  }

  /**
   * Transform add-items request to API format.
   */
  private transformAddItemsRequest(request: AddToShoppingListRequest): AddToShoppingListRequest {
    return {
      mode: request.mode,
      items: request.items.map(item => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
      })),
    };
  }

  /**
   * Transform update request to API format (snake_case fields).
   */
  private transformUpdateItemRequest(request: UpdateShoppingListItemRequest): Record<string, unknown> {
    return {
      ...(request.isChecked !== undefined ? { is_checked: request.isChecked } : {}),
      ...(request.quantity !== undefined ? { quantity: request.quantity } : {}),
      ...(request.name !== undefined ? { name: request.name } : {}),
      ...(request.unit !== undefined ? { unit: request.unit } : {}),
    };
  }

  /**
   * Normalize unknown errors into ShoppingListApiError.
   */
  private createErrorFromUnknown(error: unknown, operation: string): ShoppingListApiError {
    if (error instanceof ShoppingListApiError) {
      return error;
    }

    if (error instanceof BaseApiError) {
      return this.createDomainError(
        operation,
        error.message,
        error.status,
        error.fieldErrors,
        error.code
      );
    }

    return this.createDomainError(
      operation,
      `Shopping list request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined
    );
  }
}

/**
 * Singleton instance of ShoppingListDomain
 */
export const shoppingListDomain = new ShoppingListDomain();
